import { useState, useEffect, useRef, useCallback } from 'react'
import { message } from 'antd'
import {
  assembleScenesWithAudio,
  assembleScenesWithAudioAndDialogue,
  assembleScenesDialogueOnly,
  getTaskStatus
} from '../../../../../services/api/aiVideoGeneration'
import { constructAssemblyPayload } from '../../../utils/videoUtils'

export const useFinalVideoGeneration = ({
  formData,
  setFormData,
  videoId,
  onPersistVideoData
}) => {
  const [isGeneratingFinalVideo, setIsGeneratingFinalVideo] = useState(false)
  const [finalVideoError, setFinalVideoError] = useState(null)

  // Ref to track if component is mounted for long-running async ops
  const isMountedRef = useRef(true)
  const formDataRef = useRef(formData)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  const updateFormDataAndPersist = useCallback(
    async updater => {
      const nextData =
        typeof updater === 'function' ? updater(formDataRef.current) : updater

      formDataRef.current = nextData
      setFormData(nextData)

      if (onPersistVideoData) {
        try {
          await onPersistVideoData(nextData)
        } catch (error) {
          console.error('Error persisting final video data:', error)
        }
      }

      return nextData
    },
    [onPersistVideoData, setFormData]
  )

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Helper to wait for task success (Polling)
  const waitForVideoTaskSuccess = useCallback(async (taskId, onProgress) => {
    try {
      if (!taskId) return null
      const delayMs = 10000

      while (isMountedRef.current) {
        try {
          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status || null

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (status === 'success') {
            return data?.output || ''
          }
          if (status === 'failed' || status === 'error') {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            // If pending but progress is 100%, retry immediately/quickly
            await new Promise(resolve => setTimeout(resolve, 1000))
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          // Continue polling even on error, transient network issues
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
      return null
    } catch (err) {
      return null
    }
  }, [])

  // Main function to start generation
  const generateFinalVideo = useCallback(
    async (orderedClips = null) => {
      try {
        setIsGeneratingFinalVideo(true)
        setFinalVideoError(null)

        const scenes = formData.shotList || []

        let targetData = scenes
        if (orderedClips && orderedClips.length > 0) {
          const volumeMap = {}
          const shotDataMap = {} // preserve shot fields not present on orderedClips
          scenes.forEach(scene => {
            ;(scene.shots || []).forEach(shot => {
              const key = `${scene.id}-${shot.id || shot.shotId || shot.key}`
              volumeMap[key] = shot.volume
              shotDataMap[key] = shot
            })
          })

          const clipsByScene = {}
          orderedClips.forEach(clip => {
            if (!clipsByScene[clip.sceneId]) clipsByScene[clip.sceneId] = []
            const shotKey = `${clip.sceneId}-${clip.shotId}`
            const originalShot = shotDataMap[shotKey] || {}
            clipsByScene[clip.sceneId].push({
              ...originalShot,
              ...clip,
              volume: volumeMap[shotKey],
              video_duration:
                clip.video_duration || originalShot.video_duration || '',
              generated_video_duration:
                clip.generated_video_duration ||
                originalShot.generated_video_duration ||
                ''
            })
          })

          targetData = scenes
            .map(scene => ({
              ...scene,
              shots: clipsByScene[scene.id] || []
            }))
            .filter(scene => scene.shots.length > 0)
        }

        // Use orderedClips if provided, otherwise fallback to scenes
        // We need to construct the payload helper inside or import it
        // constructAssemblyPayload is imported
        const payload = constructAssemblyPayload(
          targetData,
          formData.locationMap,
          true, // isFinalVideo
          formData.transitions // Pass transitions from formData
        )
        if (videoId) {
          payload.video_id = videoId
        }

        console.log('payload assembleScenesWithAudio againnnn', payload)

        const response =
          formData.audioMode === 'dialogue'
            ? await assembleScenesDialogueOnly(payload)
            : formData.audioMode === 'narration'
            ? await assembleScenesWithAudio(payload)
            : await assembleScenesWithAudioAndDialogue(payload)
        const taskId = response?.data?.task_id

        if (!taskId) {
          throw new Error('Video generation did not return a task id')
        }

        await updateFormDataAndPersist(prev => ({
          ...prev,
          video: {
            id: taskId,
            status: 'pending'
          }
        }))

        return taskId
      } catch (error) {
        console.error('Error generating final video:', error)
        message.error('Failed to generate final video: ' + error.message)
        setFinalVideoError(error?.message || 'Unknown error occurred')
        setIsGeneratingFinalVideo(false)
        return null
      }
    },
    [
      formData.shotList,
      formData.locationMap,
      formData.transitions,
      formData.audioMode,
      videoId,
      updateFormDataAndPersist
    ]
  )

  // Polling Effect
  useEffect(() => {
    const { id, status } = formData.video || {}

    if (id && status === 'pending') {
      setIsGeneratingFinalVideo(true)
      setFinalVideoError(null)

      const pollForVideo = async () => {
        try {
          const output = await waitForVideoTaskSuccess(id)

          if (!isMountedRef.current) return

          if (output) {
            const finalVideos = output?.final_videos || {}
            const finalVideo =
              finalVideos.full_video || Object.values(finalVideos)[0]

            if (finalVideo) {
              await updateFormDataAndPersist(prev => {
                const newHistoryEntry = {
                  date: new Date().toISOString(),
                  url: finalVideo,
                  hasMusic: false
                }
                const existingHistory = Array.isArray(prev.final_videos_history)
                  ? prev.final_videos_history
                  : []
                const updatedHistory = [...existingHistory, newHistoryEntry]

                return {
                  ...prev,
                  finalVideo,
                  final_videos_history: updatedHistory,
                  video: {
                    ...prev.video,
                    status: 'completed'
                  }
                }
              })
              setIsGeneratingFinalVideo(false)
              return
            }
          }

          // If we get here, it implies failure or no output
          throw new Error('Video generation failed')
        } catch (error) {
          if (!isMountedRef.current) return
          console.error('Polling error:', error)
          setFinalVideoError(error.message || 'Generation failed')

          await updateFormDataAndPersist(prev => ({
            ...prev,
            video: {
              ...prev.video,
              status: 'failed'
            }
          }))

          setIsGeneratingFinalVideo(false)
        }
      }

      pollForVideo()
    }
  }, [
    formData.video?.id,
    formData.video?.status,
    waitForVideoTaskSuccess,
    updateFormDataAndPersist
  ])

  return {
    isGeneratingFinalVideo,
    finalVideoError,
    generateFinalVideo,
    setFinalVideoError
  }
}
